/**
 * The logic behind the sandbox input editor (LO-189), without React or a
 * network, so it can be read and tested on its own.
 *
 * ## What this keeps, and what it deliberately does not
 *
 * Nothing here knows what a type is. Which types exist, which fields each one
 * has, and whether a field is an enum, a list, an integer in minor units or a
 * timestamp all come from `GET /schema`, passed in — so a type the kernel adds
 * tomorrow is editable with no change to this file (`README.md`: *render from
 * the kernel, never from a hardcoded copy*). The only thing this derives from
 * a field is **how to type into it**.
 *
 * It does not decide what is *allowed*. Required fields, formats, enum
 * membership, ranges, a tag the type cannot carry, a persona outside the
 * ceiling, a host that is not a hostname: the kernel refuses each with a
 * sentence naming the thing, and the screen shows that sentence. A second copy
 * of those rules here would be correct on the day it was written and wrong the
 * first time somebody changed the real one. What this does catch is the one
 * thing the kernel cannot see: text that is not even the right *shape* to send
 * (an integer field holding words, JSON that does not parse).
 */

/** The tags this draft's own reads name: the ceiling a persona sits inside. */
export function ceilingTags(request) {
  const tags = new Set();
  for (const grant of request?.proposed?.reads || []) {
    for (const tag of grant.tags || []) tags.add(tag);
  }
  return [...tags].sort();
}

/** Types the draft reads, with or without a person behind the call. */
export function readTypes(request) {
  const types = new Set();
  for (const key of ["reads", "service_reads"]) {
    for (const grant of request?.proposed?.[key] || []) types.add(grant.type);
  }
  return types;
}

/**
 * The types to offer for a sample record: every type `/schema` describes, the
 * ones the request reads first. Not filtered to those, because a type the
 * request only *writes* is also reachable and the kernel is the one that knows
 * which verb touches which type — it refuses a type the request does not reach,
 * naming the ones it does.
 */
export function offeredTypes(schema, request) {
  const reads = readTypes(request);
  const types = (schema?.types || []).filter((t) => t?.type);
  const byName = (a, b) => a.type.localeCompare(b.type);
  return [
    ...types.filter((t) => reads.has(t.type)).sort(byName),
    ...types.filter((t) => !reads.has(t.type)).sort(byName),
  ].map((t) => ({ ...t, requested: reads.has(t.type) }));
}

/** How to type into a field, from what `/schema` says about it. */
export function inputKind(field) {
  if (field.enum?.length) return field.repeated ? "enums" : "enum";
  if (field.type === "object") return "json";
  if (field.type === "integer" || field.minor_units) {
    return field.repeated ? "integers" : "integer";
  }
  // string and timestamp: text, or one value per line when it is a list.
  return field.repeated ? "lines" : "text";
}

const WHOLE = /^[+-]?\d+$/;

const empty = (value) =>
  value === undefined || value === null
  || (Array.isArray(value) ? value.length === 0 : String(value).trim() === "");

const lines = (text) =>
  String(text).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);

/**
 * One field's typed text as the JSON value the kernel takes, or the reason it
 * could not be: `{ value }`, `{ skip: true }` for nothing typed, `{ error }`.
 */
export function parseField(field, raw) {
  if (empty(raw)) return { skip: true };
  const kind = inputKind(field);
  const unit = field.minor_units ? " in minor units" : "";
  const whole = (text) => WHOLE.test(text.trim()) ? Number(text.trim()) : null;
  if (kind === "integer") {
    const n = whole(String(raw));
    return n === null ? { error: `a whole number${unit}` } : { value: n };
  }
  if (kind === "integers") {
    const rows = lines(raw);
    const values = rows.map(whole);
    return values.includes(null)
      ? { error: `whole numbers${unit}, one per line` }
      : { value: values };
  }
  if (kind === "lines") return { value: lines(raw) };
  if (kind === "enums") return { value: [...raw] };
  if (kind === "json") {
    try {
      return { value: JSON.parse(String(raw)) };
    } catch {
      return { error: "JSON that does not parse" };
    }
  }
  return { value: String(raw).trim() };
}

/** A stored value back into the text a person edits. */
export function fieldText(field, value) {
  if (value === undefined || value === null) return inputKind(field) === "enums" ? [] : "";
  const kind = inputKind(field);
  if (kind === "enums") return [...value];
  if (kind === "lines" || kind === "integers") return (value || []).join("\n");
  if (kind === "json") return JSON.stringify(value, null, 2);
  return String(value);
}

let counter = 0;
/** A key that names a row in the editor and nothing else: never sent. */
export const rowKey = (prefix) => `${prefix}-${(counter += 1)}`;

/** A new, empty sample record of `typeSpec`. */
export function blankRecord(typeSpec) {
  return { key: rowKey("record"), type: typeSpec.type, id: "", tags: [], values: {} };
}

/** A stored record as an editable row. */
export function recordRow(typeSpec, type, record) {
  if (!typeSpec) return { key: rowKey("record"), type, raw: record };
  const values = {};
  for (const field of typeSpec.fields || []) {
    const text = fieldText(field, record?.fields?.[field.name]);
    if (!empty(text)) values[field.name] = text;
  }
  return {
    key: rowKey("record"), type, id: record?.id || "",
    tags: [...(record?.tags || [])], values,
  };
}

/** Every stored sample record as editable rows, in the order stored. */
export function recordRows(schema, fixtures) {
  const specs = new Map((schema?.types || []).map((t) => [t.type, t]));
  const out = [];
  for (const [type, records] of Object.entries(fixtures || {})) {
    for (const record of records || []) out.push(recordRow(specs.get(type), type, record));
  }
  return out;
}

/**
 * The sample records as the `fixtures` object the kernel takes, with where
 * each one sits so a refusal that says "Person fixture 1" can be pointed at the
 * row the person is looking at.
 *
 * `errors` is per row and per field, for the one thing checked here: that the
 * text is the right shape (see the file comment).
 */
export function fixturesBody(rows, schema) {
  const specs = new Map((schema?.types || []).map((t) => [t.type, t]));
  const fixtures = {};
  const positions = {};
  const errors = {};
  for (const row of rows) {
    const spec = specs.get(row.type);
    let record;
    if (!spec) {
      record = row.raw;
    } else {
      const fields = {};
      for (const field of spec.fields || []) {
        const parsed = parseField(field, row.values[field.name]);
        if (parsed.error) {
          errors[row.key] = { ...(errors[row.key] || {}), [field.name]: parsed.error };
        } else if (!parsed.skip) {
          fields[field.name] = parsed.value;
        }
      }
      record = { fields };
      const id = (row.id || "").trim();
      if (id) record.id = id;
      if (row.tags.length) record.tags = [...row.tags];
    }
    (fixtures[row.type] ||= []).push(record);
    (positions[row.type] ||= []).push(row.key);
  }
  return { fixtures, positions, errors };
}

/** Personas as the kernel takes them. A tag is a group in this kernel, and the
 *  kernel states both lists from the union, so one selection is sent as both. */
export function personasBody(personas) {
  return personas.map((p) => ({
    name: p.name.trim(), groups: [...p.tags], tags: [...p.tags],
  }));
}

/**
 * What the test users section says about who to write (LO-211), in an author's words.
 *
 * A person opens an app only if they hold every tag it reads or writes with, so a test user
 * who holds fewer is not a smaller version of the app to look for bugs in: they get the
 * missing-permissions screen, the checks expect that screen for them, and the draft fails if
 * the app is shown to them instead. It says the checks **ask the Kernel** who may open the
 * app: the rule is the Kernel's, and a line below that predicts it from the list is a reading
 * of the list and says so.
 */
export function personasBasis(tags) {
  if (!tags?.length) {
    return "This draft reads no tags, so no test user can be missing one: every test user can open it.";
  }
  return "A tag is a group of people in the Kernel. Only tags this draft reads are offered, because a test user is somebody inside what the app asks for. "
    + "A person who is missing a tag the app reads or writes with can't open it: they get the missing-permissions screen, which says what is missing, in place of the app. "
    + "Make one test user who holds every tag, who is tried through the whole app, and one who is missing a tag: the checks expect that screen for them, naming what they lack, and fail the draft if they are shown the app instead. "
    + "The checks ask the Kernel who may open the app; they don't work it out from this list.";
}

/**
 * What the checks will expect of one test user, from the tags this draft needs and the tags
 * the test user holds: `kind` is `all` (tried through the whole app) or `missing` (expected to
 * get the screen, and `missing` says which tags). A reading of the two lists and not the
 * Kernel's answer, which the checks ask for themselves.
 */
export function testUserLine(held, needed) {
  const have = new Set(held || []);
  const want = [...new Set(needed || [])].sort();
  const missing = want.filter((tag) => !have.has(tag));
  const holds = [...have].sort();
  if (!want.length) {
    return {
      kind: "all", missing: [],
      text: `${holds.length ? `Holds ${holds.join(", ")}` : "Holds no tags"}, and this draft needs none, so they open the whole app.`,
    };
  }
  if (!missing.length) {
    return {
      kind: "all", missing: [],
      text: `Holds ${holds.join(", ")}, every tag this draft needs. They are tried through the whole app.`,
    };
  }
  return {
    kind: "missing", missing,
    text: `${holds.length ? `Holds ${holds.join(", ")}.` : "Holds no tags."} Is missing ${missing.join(", ")}, which this draft needs to open the app. `
      + "The Kernel should turn them away with the missing-permissions screen: the checks expect that screen for them, and fail the draft if they are shown the app.",
  };
}

export const personaRows = (personas) =>
  (personas || []).map((p) => ({
    key: rowKey("persona"), name: p.name,
    tags: [...new Set([...(p.tags || []), ...(p.groups || [])])].sort(),
  }));

/** The whole edit as the body of `POST /manifest-requests/{id}/revise`. */
export function reviseBody({ egress, personas, records, schema }) {
  const sample = fixturesBody(records, schema);
  return {
    body: {
      egress: [...egress],
      personas: personasBody(personas),
      fixtures: sample.fixtures,
    },
    positions: sample.positions,
    errors: sample.errors,
  };
}

/** Whether what is on screen differs from what the kernel holds. Compared as
 *  the bodies each would send, so formatting that sends the same thing is not
 *  a change. */
export function changed(held, now) {
  return JSON.stringify(held) !== JSON.stringify(now);
}

/**
 * Where a refusal points, in the editor's own terms.
 *
 * The kernel's sentence is shown whole, always. This only finds the row it is
 * about so that row can say so too: the persona by `persona`, a sample record
 * by `type` and `index` (its position among that type's records, which is how
 * `positions` was built), and an egress host by being named in the sentence.
 */
export function refusalTarget(failure, { positions = {}, egress = [] } = {}) {
  const body = failure?.body || {};
  const target = { persona: "", record: "", hosts: [] };
  if (typeof body.persona === "string") target.persona = body.persona;
  if (typeof body.type === "string" && Number.isInteger(body.index)) {
    target.record = positions[body.type]?.[body.index] || "";
  }
  if (body.parameter === "egress") {
    const said = String(body.detail || failure?.message || "").toLowerCase();
    target.hosts = egress.filter((host) => said.includes(host.toLowerCase()));
  }
  return target;
}

/**
 * The request a draft has become: itself, or the newest one it was replaced by.
 *
 * A revision is a new draft with a new id and the old one closed as
 * `superseded` naming it, so anything holding the old id — a chat that last
 * saw the agent propose it — is looking at a closed row. Follow `superseded_by`
 * until a row that is not superseded, with a limit so a loop in the data ends.
 */
export async function followHead(fetchOne, id, limit = 12) {
  const seen = new Set();
  let current = await fetchOne(id);
  for (let hops = 0; hops < limit; hops += 1) {
    if (current?.state !== "superseded" || !current.superseded_by) break;
    if (seen.has(current.request_id)) break;
    seen.add(current.request_id);
    current = await fetchOne(current.superseded_by);
  }
  return current;
}

const STAGE_OF = { draft: "build", pending: "approval", approved: "live" };

/**
 * A chat view corrected for a request that has since been replaced.
 *
 * The chat names the request its agent last proposed, and says what state the
 * kernel holds that row in. After a revision that row is `superseded`, so the
 * Send for approval button and the draft panel go with it, though the author's
 * draft is right there under a new id. This puts the newest id and its state
 * back, the same correction `builder.with_decision` makes for the stage, and
 * only for a view that really is pointing at a replaced request.
 */
export function overlayHead(view, head) {
  if (!view || view.decision !== "superseded" || !head) return view;
  const to = head.request;
  if (!to || head.from !== view.request_id || to.state === "superseded") return view;
  const out = { ...view, request_id: to.request_id, decision: to.state };
  if (STAGE_OF[to.state]) out.stage = STAGE_OF[to.state];
  if (to.state === "rejected" || to.state === "cancelled") out.rejection = to.reason || "";
  return out;
}
